import { useState } from 'react'
import { AlertCircle, Loader, X } from 'lucide-react'
import { useSubscription } from '../context/SubscriptionContext'
import { createPortalSession } from '../lib/auth-client'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { buildResubscribeUrl } from '../lib/lander-url'

export default function SubscriptionBanner() {
  const { status, hasActiveSubscription, isAdmin, isLoading, isInTrial } = useSubscription()
  const { data: user } = useCurrentUser()
  const [loadingAction, setLoadingAction] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Hide when:
  //   - loading / admin / dismissed / no subscription ever
  //   - subscription is healthy
  //   - user is in trial (TrialBanner takes over for that state)
  if (isLoading || hasActiveSubscription || isAdmin || dismissed || status === 'none' || isInTrial) {
    return null
  }

  const handleClick = async () => {
    setLoadingAction(true)
    try {
      if (status === 'canceled') {
        // Canceled users go to the lander's /checkout page with email prefilled
        // so they land directly on plan-select.
        window.location.href = buildResubscribeUrl(user?.email)
        return
      }
      // past_due (and any other inactive-but-not-canceled state): Stripe Portal
      // is the right venue — they need to update a payment method.
      const result = await createPortalSession(window.location.href)
      window.location.href = result.url
    } catch {
      setLoadingAction(false)
    }
  }

  const getMessage = () => {
    switch (status) {
      case 'past_due':
        return 'Your payment failed. Please update your payment method to continue protecting your devices.'
      case 'canceled':
        return 'Your subscription has ended. Reactivate to continue protecting your devices.'
      default:
        return 'Your subscription is inactive. Reactivate to access all features.'
    }
  }

  const buttonLabel = status === 'canceled' ? 'Reactivate' : 'Update Payment'

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-amber-50 border-t border-amber-200 px-4 py-3 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800 truncate">{getMessage()}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleClick}
            disabled={loadingAction}
            className="px-4 py-2 bg-brand text-white text-sm font-medium rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loadingAction ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              buttonLabel
            )}
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-lg transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
